<template>
  <ProductTitledBlock
    v-if="relatedAssets && relatedAssets.length > 0"
    class="mb-8 mt-6 md:pr-[30px] print:mx-0"
    icon=""
    :title="model?.title || $t('custom.product_details.documents_block_title')"
  >
    <div class="grid grid-cols-2 gap-4 lg:grid-cols-3">
      <template v-for="item in relatedAssets" :key="item.id">
        <template v-for="asset in item.assets" :key="asset.id">
          <div
            v-if="asset.url"
            class="relative block rounded-lg border border-[color:var(--color-border)] bg-[#F8F8F8] px-4 pt-4"
          >
            <div class="bg-white">
              <a class="text-[0px]" :href="asset.url" target="_blank">
                <VcImage
                  v-if="item?.images[0]?.url"
                  :src="item?.images[0]?.url"
                  size-suffix="sm"
                  lazy
                  class="h-[150px] w-full object-contain"
                  :alt="asset.name"
                />

                <VcImage
                  v-else
                  :src="NO_IMAGE_URL"
                  size-suffix="sm"
                  lazy
                  class="h-[150px] w-full object-contain"
                  :alt="asset.name"
                />

                {{ asset.name }}
              </a>

              <div v-if="storeId === 'HerasIntern' && isImageFile(asset.mimeType)" class="z-20 px-2">
                <VcButton class="mt-2 w-full" color="primary" size="sm" @click="onFileDownloadHighQuality(asset)">
                  {{ $t("pages.product.downloads.high_resolution") }}
                </VcButton>

                <VcButton class="mt-2 w-full" color="primary" size="sm" @click="onFileDownloadLowQuality(asset)">
                  {{ $t("pages.product.downloads.low_resolution") }}
                </VcButton>

                <span class="mt-4 block">{{ $t("pages.product.downloads.different_resolution") }}</span>

                <InnoDownloadForm :asset-url="asset.url" :name="asset.name!" />
              </div>
              <div v-else-if="storeId === 'HerasIntern'">
                <VcButton class="mt-2 w-full" color="primary" size="sm" @click="onFileDownloadHighQuality(asset)">
                  {{ $t("pages.product.downloads.download_file") }}
                </VcButton>
              </div>
              <div v-else-if="storeId === 'HerasIntern'">
                <VcButton class="mt-2 w-full" color="primary" size="sm" @click="onFileDownloadHighQuality(asset)">
                  {{ $t("pages.product.downloads.download_file") }}
                </VcButton>
              </div>
            </div>
          </div>
        </template>
      </template>
    </div>
  </ProductTitledBlock>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { NO_IMAGE_URL } from "@/core/constants";
import { globals } from "@/core/globals";
import { downloadFileWithNewClient } from "@/shared/files";
import ProductTitledBlock from "../product-titled-block.vue";
import InnoDownloadForm from "./inno-download-form.vue";
import type { Product, Asset } from "@/core/api/graphql/types";
import type { PropType } from "vue";
import { useResizeImage } from "@/shared/catalog";

const props = defineProps({
  model: {
    type: Object,
  },

  relatedAssets: {
    type: Array as PropType<Product[]>,
    required: true,
  },
});

const relatedAssets = computed(() => props?.relatedAssets);
const { storeId } = globals;
const { fetchResizeImage } = useResizeImage();

function isImageFile(file: string | undefined): boolean {
  return !!file && file?.startsWith("image/");
}

function getImageDimensions(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = reject;
    img.src = url;
  });
}

function onFileDownloadHighQuality(file: Asset) {
  if (file && file.url) {
    void downloadFileWithNewClient(file.url, file.name!);
  }
}

async function onFileDownloadLowQuality(file: Asset) {
  const { width, height } = await getImageDimensions(file.url);

  const halfWidth = Math.floor(width / 2);
  const halfHeight = Math.floor(height / 2);

  const resizeData = {
    blobUrl: file.url,
    width: halfWidth,
    height: halfHeight,
    maintainAspectRatio: true,
  };

  const response = await fetchResizeImage(resizeData);

  if (response && response.blobUrl) {
    void downloadFileWithNewClient(response.blobUrl, file.name!);
  }
}
</script>
